import { useMemo, useState } from 'react'
import { Check, Search } from 'lucide-react'

import {
  filterFlexibleComboMenu,
  flexibleComboSelectionSummary,
  isFlexibleComboPackage,
  maxFlexibleComboSelections,
  toggleFlexibleComboSelection,
} from '@/lib/pos/posFlexibleCombo'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {import('@/types/api').PosRedeemablePackage} props.pkg
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {number[]} props.selectedServiceIds
 * @param {(ids: number[]) => void} props.onChange
 * @param {boolean} props.disabled
 */
export function PosFlexibleComboPicker({
  pkg,
  sale,
  selectedServiceIds,
  onChange,
  disabled = false,
}) {
  const [query, setQuery] = useState('')

  const maxSelections = maxFlexibleComboSelections(pkg, sale)
  const summary = flexibleComboSelectionSummary(pkg, selectedServiceIds, sale)

  const visibleMenu = useMemo(
    () => filterFlexibleComboMenu(pkg, query),
    [pkg, query],
  )

  if (!isFlexibleComboPackage(pkg)) {
    return null
  }

  const handleToggle = (serviceId) => {
    if (disabled) return
    onChange(toggleFlexibleComboSelection(selectedServiceIds, serviceId, maxSelections))
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-primary">
          Flexible combo · pick from menu
        </p>
        <p className="mt-1 text-base font-semibold">{pkg.package_name}</p>
        <p className="mt-1 text-[11px] text-muted-foreground">{summary.label}</p>
      </div>

      <div className="mt-3">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search menu services…"
            disabled={disabled || maxSelections === 0}
            className="w-full rounded-xl border border-input bg-background py-2 pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
      </div>

      <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
        {maxSelections === 0 ? (
          <p className="rounded-xl border border-dashed border-border/60 bg-muted/10 px-3 py-6 text-center text-[11px] text-muted-foreground">
            All picks on this package are already on the ticket or used.
          </p>
        ) : visibleMenu.length === 0 ? (
          <p className="px-2 py-6 text-center text-[11px] text-muted-foreground">
            No menu services match your search.
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {visibleMenu.map((item) => {
              const selected = selectedServiceIds.includes(item.service_id)
              const blocked = !item.is_selectable || (!selected && selectedServiceIds.length >= maxSelections)

              return (
                <li key={item.service_id}>
                  <button
                    type="button"
                    disabled={disabled || blocked}
                    onClick={() => handleToggle(item.service_id)}
                    className={cn(
                      'relative w-full rounded-xl border px-3 py-3 text-left transition-colors',
                      selected
                        ? 'border-primary/50 bg-primary/10 ring-1 ring-primary/25'
                        : 'border-border/50 bg-card hover:border-primary/20 hover:bg-muted/20',
                      blocked && !selected && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    {selected ? (
                      <span className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3 w-3" />
                      </span>
                    ) : null}
                    <p className="pr-6 text-sm font-semibold">{item.service_name}</p>
                    <p className="mt-1 text-xs font-bold text-emerald-700">AED 0.00 — covered</p>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
